import { useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { usePagination } from "@/components/common/usePagination";
import { type ComboboxOption } from "@/components/manufacturing/ManufacturingComboboxPicker";
import { updateManufacturingLaborRateItem } from "@/lib/manufacturing/laborBillingClient";
import type { MfgLaborRateItem } from "@/lib/manufacturing/laborBillingTypes";
import {
  createUnconfirmedLaborRateModel,
  linkLaborRateItemToRecipe,
} from "@/lib/manufacturing/laborCompanyRateUnconfirmed";
import type { LaborPaysBy } from "@/lib/manufacturing/laborPayQuantity";
import {
  ManufacturingLaborCompanyRateUnmatchedToolbar,
  type UnmatchedRateNeed,
} from "./ManufacturingLaborCompanyRateUnmatchedToolbar";
import { ManufacturingLaborCompanyRateUnmatchedRow } from "./ManufacturingLaborCompanyRateUnmatchedRow";
import { useEntityBulkSelection } from "./useEntityBulkSelection";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { LABOR_PAY_DEFAULT_PAGE_SIZE, LABOR_PAY_PAGE_SIZES } from "@/lib/manufacturing/laborPayPaging";

export function ManufacturingLaborCompanyRateUnmatched({
  items,
  recipes,
  busy,
  readOnly,
  search,
  onResolved,
}: {
  items: MfgLaborRateItem[];
  recipes: MfgRecipeSummary[];
  busy: boolean;
  readOnly: boolean;
  search: string;
  onResolved: () => void;
}) {
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paysByDraft, setPaysByDraft] = useState<Record<string, LaborPaysBy | "">>({});
  const [need, setNeed] = useState<UnmatchedRateNeed>("all");
  const [bulkPaysBy, setBulkPaysBy] = useState<LaborPaysBy | "">("");
  const unmatched = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = items.filter((item) => {
      if (item.recipe_id && item.pays_by) return false;
      if (need === "needs_pays_by" && item.pays_by) return false;
      if (need === "needs_sku" && item.recipe_id) return false;
      return true;
    });
    if (!q) return rows;
    return rows.filter((item) =>
      [item.label, item.item_code, item.option_code, item.notes].join(" ").toLowerCase().includes(q)
    );
  }, [items, need, search]);
  const page = usePagination(unmatched, LABOR_PAY_DEFAULT_PAGE_SIZE);
  const bulk = useEntityBulkSelection(items, unmatched);

  const options = useMemo<ComboboxOption<MfgRecipeSummary>[]>(
    () =>
      recipes
        .filter((r) => r.is_active)
        .map((r) => ({
          key: r.id,
          label: `${r.code} — ${r.name}`,
          sublabel: r.kind,
          data: r,
        })),
    [recipes]
  );

  function chosenPaysBy(item: MfgLaborRateItem): LaborPaysBy | "" {
    return paysByDraft[item.id] ?? item.pays_by ?? "";
  }

  async function persist(item: MfgLaborRateItem, patch: Parameters<typeof updateManufacturingLaborRateItem>[0]) {
    setRowBusy(item.id);
    const res = await updateManufacturingLaborRateItem(patch);
    setRowBusy(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onResolved();
  }

  async function link(item: MfgLaborRateItem, recipe: MfgRecipeSummary) {
    const pays_by = chosenPaysBy(item);
    if (!pays_by) {
      setRowBusy(null);
      setError("Choose Pays by before connecting. Do not assume quantity 1.");
      return;
    }
    setRowBusy(item.id);
    const res = await linkLaborRateItemToRecipe({ rate_item_id: item.id, recipe, pays_by });
    setRowBusy(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onResolved();
  }

  async function savePaysBy(item: MfgLaborRateItem) {
    const pays_by = chosenPaysBy(item);
    if (!pays_by) {
      setError("Choose Pays by before connecting. Do not assume quantity 1.");
      return;
    }
    await persist(item, { rate_item_id: item.id, pays_by });
  }

  function selectedNeedSku(): MfgLaborRateItem[] {
    return unmatched.filter((item) => bulk.selectedIds.has(item.id) && !item.recipe_id);
  }

  function paysByFor(item: MfgLaborRateItem): LaborPaysBy | "" {
    return chosenPaysBy(item) || bulkPaysBy;
  }

  async function applyBulkPaysBy() {
    if (!bulkPaysBy) {
      setError("Choose Pays by before applying. Do not assume quantity 1.");
      return;
    }
    const targets = unmatched.filter((item) => bulk.selectedIds.has(item.id));
    if (targets.length === 0) return;
    setRowBusy("bulk");
    setError(null);
    for (const item of targets) {
      const res = await updateManufacturingLaborRateItem({
        rate_item_id: item.id,
        pays_by: bulkPaysBy,
      });
      if (!res.ok) {
        setRowBusy(null);
        setError(res.error);
        return;
      }
    }
    bulk.clearSelection();
    setRowBusy(null);
    onResolved();
  }

  async function createUnconfirmed(item: MfgLaborRateItem) {
    const pays_by = chosenPaysBy(item);
    if (!pays_by) {
      setError("Choose Pays by before connecting. Do not assume quantity 1.");
      return;
    }
    setRowBusy(item.id);
    const res = await createUnconfirmedLaborRateModel({ item, pays_by, recipes });
    setRowBusy(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onResolved();
  }

  async function createUnconfirmedSelected() {
    const targets = selectedNeedSku();
    if (targets.length === 0) return;
    if (targets.some((item) => !paysByFor(item))) {
      setError("Choose Pays by before connecting. Do not assume quantity 1.");
      return;
    }
    setRowBusy("bulk");
    setError(null);
    const known = [...recipes];
    for (const item of targets) {
      const pays_by = paysByFor(item) as LaborPaysBy;
      const res = await createUnconfirmedLaborRateModel({ item, pays_by, recipes: known });
      if (!res.ok) {
        setRowBusy(null);
        setError(res.error);
        return;
      }
      if (!known.some((r) => r.id === res.recipe.id)) known.push(res.recipe);
    }
    bulk.clearSelection();
    setRowBusy(null);
    onResolved();
  }

  const listBusy = busy || rowBusy === "bulk";
  const openCount = items.filter((item) => !item.recipe_id || !item.pays_by).length;
  if (openCount === 0) return null;

  return (
    <SectionCard
      title="Unmatched pay items"
      description="Rates that still need a Models SKU and/or Pays by. Search, select a group, apply Pays by, and create unconfirmed models. Enumerated $0 IdeaRoom options are included in the building or package and do not get a separate pay line."
    >
      {error ? <p className="mb-2 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <ManufacturingLaborCompanyRateUnmatchedToolbar
        need={need}
        onNeed={setNeed}
        filteredCount={unmatched.length}
        selectedCount={unmatched.filter((item) => bulk.selectedIds.has(item.id)).length}
        allFilteredSelected={bulk.allFilteredSelected}
        bulkPaysBy={bulkPaysBy}
        onBulkPaysBy={setBulkPaysBy}
        busy={listBusy}
        readOnly={readOnly}
        onSelectAllFiltered={bulk.selectAllFiltered}
        onClear={bulk.clearSelection}
        onApply={() => void applyBulkPaysBy()}
        onCreateUnconfirmed={() => void createUnconfirmedSelected()}
        selectedNeedSkuCount={selectedNeedSku().length}
      />
      {unmatched.length === 0 ? (
        <p className="text-sm text-gray-500">No unmatched items for this filter.</p>
      ) : (
        <ul className="space-y-2">
          {page.paginatedItems.map((item) => (
            <ManufacturingLaborCompanyRateUnmatchedRow
              key={item.id}
              item={item}
              readOnly={readOnly}
              listBusy={listBusy}
              rowBusy={rowBusy === item.id}
              selected={bulk.selectedIds.has(item.id)}
              paysBy={chosenPaysBy(item)}
              options={options}
              onToggle={() => bulk.toggleSelected(item.id)}
              onPaysBy={(pays_by) => setPaysByDraft((prev) => ({ ...prev, [item.id]: pays_by }))}
              onSavePaysBy={() => void savePaysBy(item)}
              onLink={(recipe) => void link(item, recipe)}
              onCreateUnconfirmed={() => void createUnconfirmed(item)}
            />
          ))}
        </ul>
      )}
      {unmatched.length === 0 ? null : (
        <PaginationBar
          pageIndex={page.pageIndex}
          pageCount={page.pageCount}
          pageSize={page.pageSize}
          totalCount={page.totalCount}
          firstShown={page.firstShown}
          lastShown={page.lastShown}
          onPageChange={page.setPageIndex}
          onPageSizeChange={page.setPageSize}
          pageSizeOptions={LABOR_PAY_PAGE_SIZES}
          noun="items"
          alwaysShow
        />
      )}
    </SectionCard>
  );
}
